import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { useQuery } from "@tanstack/react-query";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { PostService } from "../services/PostService";
import { Toast } from "../../../Shared/components/Toast";
import { AuthService } from "../../Auth/services/AuthService";

export const PostPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const company = location.state?.company;
  const companyId = company;
  const companyIds = AuthService.getLocalUser()?.user?.companies?.map(
    (item) => item?.id
  );

  let queryParams = "";
  if (companyId) {
    queryParams = JSON.stringify([companyId]);
  } else if (companyIds) {
    queryParams = JSON.stringify(companyIds);
  }

  const findActivities = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: queryParams,
      };
      setIsLoading(true);
      const response = await PostService.getAll(pageOptions);
      setIsLoading(false);
      return response;
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["posts", pagination.pageIndex, pagination.pageSize],
    queryFn: findActivities,
    placeholderData: (previous) => previous?.data,
  });

  const handleSelectPost = (post: any) => {
    if (companyIds){
      navigate("/services/accounts/posts/details", { state: { post } });
    } else {
    navigate("/posts/details", { state: { post } });
    }
  };

  const posts = query?.data?.data;
  const containerPx = "max-w-screen-xl px-4 py-8x mx-auto py-4";

  return (
    <>
      {posts?.length > 0 && !isLoading ? (
        <div
          className={`overflow-y-auto ${companyId != null ? containerPx : ""}`}
          style={{ maxHeight: companyId != null ? "100vh" : "calc(3 * 120px)" }}
        >
          <div className="">
            <h1 className="text-3xl font-extrabold text-gray-900 px-4">
              Latest Posts
            </h1>
            <div className="row">
              {posts.map((post: any) => (
                <div
                  key={post.id}
                  className="overflow-hidden cursor-pointer"
                  onClick={() => handleSelectPost(post)}
                >
                  <div className="p-4">
                    <h2 className="text-xl font-semibold text-gray-800 truncate mb-2">
                      {post.name}
                    </h2>
                    <div
                      className="text-gray-600 text-sm mb-4"
                      dangerouslySetInnerHTML={{
                        __html:
                          post.description.length > 100
                            ? `${post.description.slice(0, 100)}...`
                            : post.description,
                      }}
                    ></div>
                    {post.description.length > 100 && (
                      <button className="text-blue-500 text-xs underline">
                        View More
                      </button>
                    )}
                    <div className="flex items-center text-xs text-gray-500">
                      {post.user && (
                        <span className="mr-2">
                          By {UtilService.titleCase(post.user.firstName)}{" "}
                          {UtilService.titleCase(post.user.lastName)}
                        </span>
                      )}
                      {post.company && <span>at {post.company.name}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <img
          src="https://demo.themesberg.com/landwind/images/hero.png"
          alt=""
        />
      )}
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};
